import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CalendarCheck, Scale, Droplet, Flame, Check, Utensils, MessageCircle, Heart } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ICONS = { checkin: CalendarCheck, weighin: Scale, water: Droplet, nutrition: Utensils, streak: Flame, comment: MessageCircle, like: Heart };

export default function NotificationCenter() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });
  const items = data?.items || [];
  const unread = data?.unread || 0;

  const openTarget = { checkin: "/checkin", weighin: "/body", water: "/nutrition", nutrition: "/nutrition", streak: "/checkin" };

  const markRead = async (n) => {
    if (!n.read) { await api.put(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    navigate(n.link || openTarget[n.type] || "/dashboard");
  };
  const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative grid h-9 w-9 place-items-center rounded-lg border transition-colors hover:bg-accent" data-testid="notif-btn">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground tabular-nums" data-testid="notif-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="font-display text-sm font-semibold">{t("notifications")}</span>
          {unread > 0 && (
            <button onClick={markAll} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="notif-mark-all">
              <Check className="h-3 w-3" /> {t("markAllRead")}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto no-scrollbar">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">{t("noNotifications")}</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => markRead(n)} data-testid={`notif-item-${n.id}`}
                className={cn("flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-accent", !n.read && "bg-primary/5")}>
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground"><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium">{n.title}{!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}</div>
                  <div className="text-xs text-muted-foreground">{n.body}</div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
